import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { createRole, deleteRole, fetchPermissions, fetchRoles, updateRole } from './api';
import { RoleCreateRequest, RoleUpdateRequest } from './types';

export const roleKeys = {
  all: [API_TAGS.ROLES] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  list: (filters: { isDeleted?: boolean | null } = {}) => [...roleKeys.lists(), { ...filters }] as const,
  details: () => [...roleKeys.all, 'detail'] as const,
  detail: (code: string) => [...roleKeys.details(), code] as const,
  permissions: () => [...roleKeys.all, 'permissions'] as const,
};

export const useRoles = (filters: { isDeleted?: boolean | null } = {}) => {
  return useQuery({
    queryKey: roleKeys.list(filters),
    queryFn: () => fetchRoles(filters),
  });
};

export const useInfiniteRoles = (params: { isDeleted?: boolean }) => {
  const query = useInfiniteQuery({
    queryKey: roleKeys.list(params),
    queryFn: () => fetchRoles(params),
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    getNextPageParam: () => undefined,
  });

  const data = {
    roles: query.data?.pages.flatMap((p) => p.roles) || [],
  };

  return {
    ...query,
    data,
  };
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RoleCreateRequest) => createRole(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.permissions() });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { code: string; body: RoleUpdateRequest }) => updateRole(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(variables.code) });
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
};

export const usePermissions = () => {
  return useQuery({
    queryKey: roleKeys.permissions(),
    queryFn: fetchPermissions,
  });
};
